/**
 * Scripture-first message — orchestrates the DB-driven response.
 *
 * Renders in order:
 *   1. Interactive scripture block (clickable words with Strong's)
 *   2. Pericope context (passage title)
 *   3. Analysis accordion — wraps cross-refs / word study / variants
 *   4. Commentary button / commentary section
 */

import { useMemo } from 'react';
import type { ScriptureResponse } from '@skjv/common';
import type { ProgressStep, InferenceProgress, InferenceMeta, ReportRunMeta } from '@state/chat';
import { InteractiveScriptureBlock } from './interactive-scripture-block';
import { InlineVerseRef } from './inline-verse-ref';
import { PericopeSection } from './pericope-section';
import { AnalysisSection } from './analysis-section';
import { CommentaryButton } from './commentary-button';
import { CommentarySection } from './commentary-section';
import { ReportButton } from './report-button';
import type { ReportInput } from './build-report-markdown';

interface ScriptureFirstMessageProps {
  response: ScriptureResponse;
  commentary?: string;
  /** Raw model body (pre-opener, pre-xref-inline) — preferred over
   *  `commentary` for the Report dialog's AI Response section. */
  rawModelBody?: string;
  commentaryState: 'not-requested' | 'loading' | 'loaded' | 'error';
  onRequestCommentary: () => void;
  originalQuery?: string;
  /** What the run did — the report's Metadata block. */
  reportMeta?: ReportRunMeta;
  /** The reader's "Any thoughts?" seed notes — an input the report keeps. */
  readerThoughts?: string;
  progressSteps?: ProgressStep[];
  inferenceProgress?: InferenceProgress;
  inferenceMeta?: InferenceMeta;
}

export function ScriptureFirstMessage({
  response,
  commentary,
  rawModelBody,
  commentaryState,
  onRequestCommentary,
  originalQuery,
  reportMeta,
  readerThoughts,
  progressSteps,
  inferenceProgress,
  inferenceMeta,
}: ScriptureFirstMessageProps) {
  const allWords = useMemo(
    () => response.verses.flatMap((v) => v.words),
    [response.verses],
  );

  const firstRef = response.verses[0]?.reference;

  const reportInput = useMemo<ReportInput | null>(() => {
    if (!commentary || !firstRef) return null;
    const verseText = response.verses.map((v) => v.text).join(' ');
    const firstVariant = response.variants[0];
    return {
      reference: firstRef,
      verseText,
      pericope: response.pericope
        ? {
            title: response.pericope.title,
            book: response.pericope.book,
            chapter: response.pericope.chapter,
            startVerse: response.pericope.startVerse,
            endVerse: response.pericope.endVerse,
            summary: response.pericope.summary,
          }
        : null,
      crossRefs: response.crossRefs.map((cr) => ({ ref: cr.to, text: cr.toText })),
      variantSummary: firstVariant
        ? `${firstVariant.affectedConcepts.join(', ') || firstVariant.category} (impact: ${firstVariant.theologicalImpact})`
        : null,
      aiResponse: rawModelBody ?? commentary,
      meta: reportMeta,
      readerThoughts,
    };
  }, [commentary, rawModelBody, reportMeta, readerThoughts, firstRef, response]);

  return (
    <div className="scripture-first-message">
      {firstRef && (
        <div className="scripture-verse-link">
          <InlineVerseRef reference={firstRef} displayText={firstRef} />
        </div>
      )}

      {response.verses.map((verse) => (
        <InteractiveScriptureBlock key={verse.reference} verse={verse} />
      ))}

      {response.pericope && <PericopeSection pericope={response.pericope} />}

      <AnalysisSection
        crossRefs={response.crossRefs}
        words={allWords}
        variants={response.variants}
      />

      {commentary && <CommentarySection commentary={commentary} />}

      <CommentaryButton
        state={commentaryState}
        onClick={onRequestCommentary}
        progressSteps={progressSteps}
        inferenceProgress={inferenceProgress}
        inferenceMeta={inferenceMeta}
      />

      {reportInput && <ReportButton input={reportInput} originalQuery={originalQuery} />}
    </div>
  );
}
